import React, { useState, useEffect, useCallback } from 'react';
import { 
  ISelectComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormSelectProps {
  id: string;
  properties: ISelectComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string | string[];
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormSelect: React.FC<DfFormSelectProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  showCommentsInPreview = false
}) => {
  const [selectedValue, setSelectedValue] = useState<string | string[]>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  // Check if multiple selection is enabled
  const isMultiple = properties?.validation?.multiple || false;

  // Validate field
  const validateField = useCallback((value: string | string[]): { isValid: boolean; errors: Record<string, boolean> } => {
    const errors: Record<string, boolean> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required) {
      if (isMultiple) {
        if (!Array.isArray(value) || value.length === 0) {
          errors.required = true;
          isValid = false;
        }
      } else {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors.required = true;
          isValid = false;
        }
      }
    }

    return { isValid, errors };
  }, [properties, isMultiple]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string | string[]) => {
    setSelectedValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle select change
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isMultiple) {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      handleValueChange(selectedOptions);
    } else {
      handleValueChange(e.target.value);
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (JSON.stringify(formValue) !== JSON.stringify(selectedValue)) {
      setSelectedValue(formValue);
    }
  }, [formValue, selectedValue]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get select classes
  const getSelectClasses = (): string => {
    const base = 'form-select';
    const validation = validateField(selectedValue);
    const error = !validation.isValid && isTouched ? 'is-invalid' : '';
    return `${base} ${error} ${className}`.trim();
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <select
          id={id}
          value={isMultiple ? selectedValue as string[] : selectedValue as string}
          onChange={handleSelectChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={shouldDisable}
          multiple={isMultiple}
          className={getSelectClasses()}
        >
          {!isMultiple && (
            <option value="">
              {properties.basic.placeholder || 'Select an option'}
            </option>
          )}
          {(properties.options || []).map((option, index) => (
            <option 
              key={index} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(selectedValue)}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormSelect;
